import { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from "react";
import { apiFetch, setUnauthorizedHandler } from "@/lib/api";

/**
 * Đăng nhập cho bản mobile của MVP.
 *
 * Dùng đúng API của hệ thống chính: POST /api/auth/login trả về token Sanctum,
 * GET /api/auth/me để khôi phục phiên khi mở lại ứng dụng.
 *
 * Bản gốc (hệ smt.dlhf.vn) được giữ ở AuthContext.smt.bak.tsx để tham chiếu.
 */

// ─── Types ────────────────────────────────────────────────────────────────────

export interface AuthUser {
    id: string;
    name: string;
    email: string;
    avatar: string;
    role: string;
    /** Giữ lại cho các màn hình cũ còn tham chiếu; MVP không dùng cửa hàng. */
    storeIds: string[];
}

/**
 * MVP không có mô hình cửa hàng. Kiểu này giữ lại để những màn hình của hệ cũ
 * (chưa gỡ khỏi thư mục) vẫn biên dịch được; danh sách luôn rỗng.
 */
export interface Store {
    id: string;
    code: string;
    name: string;
    region_code: string;
    contract: string;
    total_stock_products: number;
    total_stock_quantity: number;
    address?: string | null;
    ward_code?: number | null;
    ward_name?: string | null;
    province_code?: number | null;
    province_name?: string | null;
    note?: string | null;
    image?: string | null;
    contact_name?: string | null;
    contact_phone?: string | null;
}

interface AuthContextValue {
    user: AuthUser | null;
    stores: Store[];
    isAuthLoading: boolean;
    login: (loginId: string, password: string, remember?: boolean) => Promise<string | null>;
    logout: () => Promise<void>;
    updateAvatar: (avatarDataUrl: string) => void;
    updateStoreAddress: (storeId: string, patch: Partial<Store>) => void;
    getToken: () => string | null;
}

const TOKEN_KEY = "mvp_auth_token";

const AuthContext = createContext<AuthContextValue | null>(null);

/** Payload người dùng do /api/auth/login và /api/auth/me trả về. */
type ApiUser = {
    id: number | string;
    name?: string;
    fullName?: string;
    email?: string;
    avatar?: string | null;
    role?: string;
    roles?: string[];
};

function toAuthUser(u: ApiUser): AuthUser {
    return {
        id: String(u.id),
        name: u.fullName || u.name || "",
        email: u.email ?? "",
        avatar: u.avatar ?? "",
        role: u.role || u.roles?.[0] || "staff",
        storeIds: [],
    };
}

export function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<AuthUser | null>(null);

    const getToken = useCallback(
        () => localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY),
        [],
    );

    // Có token sẵn thì phải chờ xác thực xong mới biết còn hiệu lực hay không.
    const [isAuthLoading, setIsAuthLoading] = useState<boolean>(() => Boolean(
        localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY),
    ));

    const clearToken = useCallback(() => {
        localStorage.removeItem(TOKEN_KEY);
        sessionStorage.removeItem(TOKEN_KEY);
    }, []);

    /*
     * Bất kỳ lời gọi API nào nhận 401 cũng xoá phiên ngay tại máy. Các màn hình
     * đứng sau RequireAuth sẽ tự đưa người dùng về màn đăng nhập, thay vì để họ
     * thao tác tiếp trên một phiên đã chết.
     */
    useEffect(() => {
        setUnauthorizedHandler(() => {
            clearToken();
            setUser(null);
            setIsAuthLoading(false);
        });

        return () => setUnauthorizedHandler(null);
    }, [clearToken]);

    /** Khôi phục phiên khi mở lại ứng dụng: token hỏng thì xoá và về màn đăng nhập. */
    useEffect(() => {
        const token = getToken();

        if (!token) {
            setIsAuthLoading(false);
            return;
        }

        let cancelled = false;

        void (async () => {
            try {
                const res = await apiFetch<{ user?: ApiUser } & ApiUser>("/auth/me", {}, token);
                if (!cancelled) setUser(toAuthUser(res.user ?? res));
            } catch {
                if (!cancelled) {
                    clearToken();
                    setUser(null);
                }
            } finally {
                if (!cancelled) setIsAuthLoading(false);
            }
        })();

        return () => { cancelled = true; };
    }, [getToken, clearToken]);

    const login = useCallback(async (loginId: string, password: string, remember = false): Promise<string | null> => {
        try {
            // `device: mobile` để máy chủ chỉ xoay token của máy cầm tay —
            // đăng nhập ở đây không được làm bản máy tính văng ra.
            const res = await apiFetch<{ token: string; user: ApiUser }>("/auth/login", {
                method: "POST",
                body: JSON.stringify({ email: loginId, password, device: "mobile" }),
            });

            if (!res.token) return "Đăng nhập thất bại.";

            // Nhớ đăng nhập thì giữ qua các lần mở máy, không thì hết phiên là mất.
            (remember ? localStorage : sessionStorage).setItem(TOKEN_KEY, res.token);
            setUser(toAuthUser(res.user));
            setIsAuthLoading(false);

            return null;
        } catch (err: unknown) {
            const apiErr = err as { message?: string };

            return apiErr?.message ?? "Email hoặc mật khẩu không đúng.";
        }
    }, []);

    const logout = useCallback(async () => {
        const token = getToken();

        if (token) {
            try {
                await apiFetch("/auth/logout", { method: "POST" }, token);
            } catch {
                // Gọi hỏng vẫn phải xoá phiên ở máy.
            }
        }

        clearToken();
        setUser(null);
        setIsAuthLoading(false);
    }, [getToken, clearToken]);

    const updateAvatar = useCallback((avatarDataUrl: string) => {
        setUser((prev) => (prev ? { ...prev, avatar: avatarDataUrl } : prev));
    }, []);

    // MVP không có cửa hàng; giữ hàm rỗng cho các màn hình cũ.
    const updateStoreAddress = useCallback(() => {}, []);

    const value = useMemo<AuthContextValue>(() => ({
        user,
        stores: [],
        isAuthLoading,
        login,
        logout,
        updateAvatar,
        updateStoreAddress,
        getToken,
    }), [user, isAuthLoading, login, logout, updateAvatar, updateStoreAddress, getToken]);

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
    const ctx = useContext(AuthContext);

    if (!ctx) throw new Error("useAuth phải nằm trong AuthProvider");

    return ctx;
}
